import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, take } from 'rxjs';

import { SnackbarService } from '../../core/services';
import { TaskApi } from '../task/task.api';
import * as TaskActions from '../task/task.actions';
import type { ApiResponse, TaskItem, TaskProject } from '../task/task.models';
import { selectTaskProjects } from '../task/task.selectors';
import * as TaskReferenceActions from './task-reference.actions';
import type { TaskReferenceItem } from './task-reference.models';

@Injectable()
export class TaskReferenceEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(TaskApi);
  private readonly store = inject(Store);
  private readonly snackbar = inject(SnackbarService);

  loadTaskReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskReferenceActions.TaskReferenceLoadRequest),
      switchMap(() =>
        this.store.select(selectTaskProjects).pipe(
          take(1),
          switchMap((projects) =>
            this.api.listTasks().pipe(
              map((response: ApiResponse<TaskItem[]>) => TaskReferenceActions.TaskReferenceLoadSuccess({
                items: this.toTaskReferenceItems(response.data ?? [], projects ?? []),
              })),
              catchError((error) => of(TaskReferenceActions.TaskReferenceLoadFailure({
                error: this.toErrorMessage(error),
              }))),
            ),
          ),
        ),
      ),
    ),
  );

  refreshTaskReferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        TaskActions.TaskCreateSuccess,
        TaskActions.TaskUpdateSuccess,
        TaskActions.TaskExternalChangeReceived,
        TaskActions.TaskProjectsFetchSuccess,
        TaskActions.TaskProjectCreateSuccess,
        TaskActions.TaskProjectUpdateSuccess,
      ),
      map(() => TaskReferenceActions.TaskReferenceLoadRequest()),
    ),
  );

  loadTaskReferencesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(TaskReferenceActions.TaskReferenceLoadFailure),
        map(({ error }) => {
          this.snackbar.showNotification('error', '', `Failed to load task references: ${error}`, 'Close');
        }),
      ),
    { dispatch: false },
  );

  private toTaskReferenceItems(tasks: TaskItem[], projects: TaskProject[]): TaskReferenceItem[] {
    const activeProjectIds = new Set(
      (projects ?? [])
        .filter((project) => !project.is_archived && project.id?.trim())
        .map((project) => project.id.trim()),
    );

    return tasks
      .filter((task) =>
        !task.is_archived
        && task.id?.trim()
        && task.project_id?.trim()
        && task.title?.trim()
        && activeProjectIds.has(task.project_id.trim()),
      )
      .map((task) => ({
        taskId: task.id.trim(),
        projectId: task.project_id.trim(),
        title: task.title.trim(),
        taskCode: task.task_code?.trim().toUpperCase() || null,
      }))
      .sort((left, right) => {
        const leftCode = left.taskCode?.trim() || '';
        const rightCode = right.taskCode?.trim() || '';
        if (leftCode && rightCode && leftCode !== rightCode) {
          return leftCode.localeCompare(rightCode);
        }

        return left.title.localeCompare(right.title);
      });
  }

  private toErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object') {
      if ('error' in error) {
        const nested = (error as { error?: { message?: unknown } }).error?.message;
        if (typeof nested === 'string') {
          return nested;
        }
      }

      if ('message' in error) {
        const message = (error as { message?: unknown }).message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }

    return 'Unknown error';
  }
}
